import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { serviceAutoReplyService } from "@/services/service-auto-reply-service";
import type {
  ServiceAutoReplySettings,
  UpdateServiceAutoReplySettingsRequest,
} from "@/types/serviceAutoReply";

const serviceAutoReplyKeys = {
  all: ["serviceAutoReplies"] as const,
  list: (hotelId: string) =>
    [...serviceAutoReplyKeys.all, "list", hotelId] as const,
};

export function useServiceAutoReplies(hotelId: string) {
  return useQuery({
    queryKey: serviceAutoReplyKeys.list(hotelId),
    queryFn: () => serviceAutoReplyService.getAll(hotelId),
    enabled: Boolean(hotelId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateServiceAutoReply(hotelId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      serviceId,
      data,
    }: {
      serviceId: string;
      data: UpdateServiceAutoReplySettingsRequest;
    }) => serviceAutoReplyService.update(hotelId, serviceId, data),
    onSuccess: (updatedSettings) => {
      queryClient.setQueryData(
        serviceAutoReplyKeys.list(hotelId),
        (current: ServiceAutoReplySettings[] | undefined) =>
          current?.map((settings) =>
            settings.serviceId === updatedSettings.serviceId
              ? updatedSettings
              : settings,
          ),
      );
    },
  });
}
